/**
 * TaskStore — IndexedDB-backed Task CRUD
 *
 * F 72 MVP phase 1.
 *
 * Architecture:
 *   - Stores Tasks in IndexedDB 'tasks' object store (added in IndexedDBAdapter v2)
 *   - Key format: 'task:{id}' — matches the prefix routing in pickStore()
 *   - Functions: save, load, list, delete, query (status / due / listId / parentId)
 *   - EventBus integration: emits 'task:created', 'task:updated', 'task:deleted'
 *
 * Why a dedicated store rather than reusing StorageAdapter directly?
 *   - StorageAdapter is generic (any key→any store). TaskStore enforces the
 *     Task shape via isTask() validation on save and load.
 *   - Query helpers (`query({status, due})`) walk the IDB indexes instead of
 *     fetching-then-filtering, which matters once we have hundreds of tasks.
 *   - Future migrations (sub-tasks, recurrence expansion) live here without
 *     touching StorageAdapter or IndexedDBAdapter.
 */

import type { Task, TaskStatus } from '../../types/models/Task';
import { isTask, markDone, reopenTask } from '../../types/models/Task';
import { EventBus } from '@core/event-bus';
import { storage } from '@core/storage-adapter';

const KEY_PREFIX = 'task:';

export interface TaskQuery {
  status?: TaskStatus | TaskStatus[];
  listId?: string;
  parentId?: string | null;
  /** Inclusive lower bound for due date (ISO YYYY-MM-DD). */
  dueBefore?: string;
  /** Inclusive upper bound for due date (ISO YYYY-MM-DD). */
  dueAfter?: string;
}

export type TaskEvent =
  | { type: 'task:created'; task: Task }
  | { type: 'task:updated'; task: Task }
  | { type: 'task:deleted'; taskId: string };

export type TaskEventHandler = (event: TaskEvent) => void;

export interface TaskStoreLike {
  save(key: string, value: unknown): Promise<void>;
  load(key: string): Promise<unknown>;
  delete(key: string): Promise<void>;
  list(prefix: string): Promise<string[]>;
}

/**
 * Default TaskStore. Uses the global indexeddb adapter registered in
 * @core/indexeddb-adapter (storage.registerAdapter('indexeddb', ...)).
 */
export class TaskStore {
  private adapter: TaskStoreLike;
  /** In-process bus for emit/subscribe. Created lazily so the store is
   * usable without external wiring. When a host `EventBus` is provided
   * via the constructor, we use it directly. */
  private bus: MinimalBus;
  /** Map of subscription handler → host-bus reference (for cleanup). */
  private hostHandlers: Map<TaskEventHandler, { event: string; wrapped: (data: any) => void }> = new Map();

  constructor(adapter?: TaskStoreLike, eventBus?: EventBus) {
    this.adapter = adapter ?? defaultAdapter();
    // Lazy — created on first subscribe() so consumers without a host bus
    // can still observe. When a host bus is provided, we delegate.
    if (eventBus) {
      this.bus = wrapHostBus(eventBus);
    } else {
      this.bus = createFallbackBus();
    }
  }

  /** Save (create or update) a Task. Validates shape via isTask. */
  async save(task: Task): Promise<Task> {
    if (!isTask(task)) {
      throw new Error(`TaskStore.save: invalid task shape (id=${(task as Task)?.id})`);
    }
    const now = new Date().toISOString();
    const toPersist: Task = { ...task, updatedAt: now };
    await this.adapter.save(`${KEY_PREFIX}${task.id}`, toPersist);
    this.bus.emit('task:event', { type: 'task:updated', task: toPersist });
    return toPersist;
  }

  /** Load a single Task by id. Returns null if not found or not a valid Task shape. */
  async load(id: string): Promise<Task | null> {
    const raw = await this.adapter.load(`${KEY_PREFIX}${id}`);
    if (!raw) return null;
    if (!isTask(raw)) {
      // Tolerate malformed records — caller can decide to delete or surface.
      return null;
    }
    return raw;
  }

  /** List all Task ids (raw storage keys without the prefix). */
  async listIds(): Promise<string[]> {
    const keys = await this.adapter.list(KEY_PREFIX);
    return keys.map((k) => k.slice(KEY_PREFIX.length));
  }

  /** List all Tasks (unfiltered). */
  async list(): Promise<Task[]> {
    const ids = await this.listIds();
    const tasks: Task[] = [];
    for (const id of ids) {
      const t = await this.load(id);
      if (t) tasks.push(t);
    }
    return tasks;
  }

  /** Delete a Task by id. No-op if not found. */
  async delete(id: string): Promise<void> {
    await this.adapter.delete(`${KEY_PREFIX}${id}`);
    this.bus.emit('task:event', { type: 'task:deleted', taskId: id });
  }

  /**
   * Query Tasks with optional filters. Filtering is in-memory after a
   * full list() — adequate for v1 (≤1000 tasks expected). A future v1.1
   * should add an index-backed variant via IDBKeyRange.
   */
  async query(q: TaskQuery): Promise<Task[]> {
    const all = await this.list();
    return all.filter((t) => matchesQuery(t, q));
  }

  /** Convenience: mark a task done. Returns the updated task or null if not found. */
  async complete(id: string): Promise<Task | null> {
    const t = await this.load(id);
    if (!t) return null;
    return this.save(markDone(t));
  }

  /** Convenience: re-open a done/cancelled task. Returns the updated task or null. */
  async reopen(id: string): Promise<Task | null> {
    const t = await this.load(id);
    if (!t) return null;
    return this.save(reopenTask(t));
  }

  /**
   * F 86 — Drag-reorder support. Updates the `order` field of one task to a
   * new value; sibling tasks in the same view get their `order` field
   * nudged to make space.
   *
   * The reorder algorithm:
   *   1. Load all tasks in the same view (status-filtered).
   *   2. Sort by current `order` then `createdAt` (matches TasksView sort).
   *   3. Find the task being moved; if missing, no-op.
   *   4. Insert it at targetIndex; assign sparse orders (100, 200, 300, ...)
   *      so future inserts don't force a full re-pack.
   *   5. Save the moved task; siblings whose order actually changed are
   *      saved too (no-op writes are skipped).
   *
   * Returns the updated task. Other tasks are saved as a side effect; callers
   * should rely on the subscribe() channel to refresh their UI.
   */
  async reorder(
    id: string,
    targetIndex: number,
    statusFilter: TaskStatus | TaskStatus[] | null = null,
  ): Promise<Task | null> {
    const all = await this.list();
    const allowed = statusFilter
      ? (Array.isArray(statusFilter) ? statusFilter : [statusFilter])
      : null;
    const siblings = allowed
      ? all.filter((t) => allowed.includes(t.status))
      : all;
    const sorted = siblings
      .slice()
      .sort((a, b) => {
        const od = (a.order ?? 0) - (b.order ?? 0);
        if (od !== 0) return od;
        return a.createdAt.localeCompare(b.createdAt);
      });
    const fromIndex = sorted.findIndex((t) => t.id === id);
    if (fromIndex === -1) return null;
    const [moved] = sorted.splice(fromIndex, 1);
    const clamped = Math.max(0, Math.min(targetIndex, sorted.length));
    sorted.splice(clamped, 0, moved);
    // Sparse order assignment (step 1000) keeps room for inserts without
    // forcing a full re-pack on every drag.
    const updates: Array<{ task: Task; newOrder: number }> = [];
    sorted.forEach((t, idx) => {
      const newOrder = (idx + 1) * 1000;
      if (t.order !== newOrder) {
        updates.push({ task: t, newOrder });
      }
    });
    // Persist moved task last so subscribers see the final state.
    let result: Task | null = null;
    const now = new Date().toISOString();
    for (const { task, newOrder } of updates) {
      const updated = await this.save({ ...task, order: newOrder, updatedAt: now });
      if (task.id === id) result = updated;
    }
    if (!result) {
      // The moved task's order didn't change (target == source). Still
      // return the current task so the caller has something to bind to.
      result = await this.load(id);
    }
    return result;
  }

  /**
   * Subscribe to task events. Returns an unsubscribe function.
   * Internally routes through a MinimalBus that adapts to either:
   *   - a host EventBus (constructor-injected), or
   *   - a fallback in-process bus (created on first call without a host bus)
   */
  subscribe(handler: TaskEventHandler): () => void {
    const wrapped = (data: any) => {
      if (data && typeof data === 'object' && 'type' in data) {
        handler(data as TaskEvent);
      }
    };
    this.bus.on('task:event', wrapped);
    return () => this.bus.off('task:event', wrapped);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// helpers
// ─────────────────────────────────────────────────────────────────────────

function matchesQuery(task: Task, q: TaskQuery): boolean {
  if (q.status) {
    const allowed = Array.isArray(q.status) ? q.status : [q.status];
    if (!allowed.includes(task.status)) return false;
  }
  if (q.listId !== undefined && task.listId !== q.listId) return false;
  if (q.parentId !== undefined && task.parentId !== q.parentId) return false;
  if (q.dueAfter && (!task.due || task.due < q.dueAfter)) return false;
  if (q.dueBefore && (!task.due || task.due > q.dueBefore)) return false;
  return true;
}

/** Resolve the default IndexedDB adapter instance from the global storage registry. */
function defaultAdapter(): TaskStoreLike {
  // F 77 fix: was `require('@core/storage-adapter')` — broke in browser bundle.
  // Static import (above) keeps Vite happy. Tests pass adapter explicitly via
  // the TaskStore constructor, so no swap-on-import is needed.
  return storage.getDefaultAdapter() as TaskStoreLike;
}

// ─────────────────────────────────────────────────────────────────────────
// event-bus adapters
// ─────────────────────────────────────────────────────────────────────────

interface MinimalBus {
  on(event: string, handler: (payload: any) => void): void;
  off(event: string, handler: (payload: any) => void): void;
  emit(event: string, payload: any): void;
}

/** In-process fallback bus used when no host EventBus is provided.
 *
 *  F 195: this is created ONCE per process and shared, not per TaskStore.
 *
 *  Why: QuickCaptureModal and TasksView each construct their own TaskStore
 *  (`new TaskStore()`, no adapter, no host bus). With a per-instance bus the
 *  modal saved to its private bus while TasksView subscribed to a different
 *  one, so `task:event` never crossed between them. The consequence was
 *  silent and nasty: the task WAS written to storage and WAS listed on the
 *  next page load, but an already-open TasksView never refreshed — it sat
 *  on a stale count until the operator reloaded. That is the "tasks are
 *  created but unreachable" symptom, and it is a wiring bug, not a missing
 *  component.
 *
 *  Passing an explicit `eventBus` still takes precedence and is unaffected.
 *  A shared bus is only the fallback for callers that did not supply one.
 */
let sharedFallbackBus: MinimalBus | null = null;

function createFallbackBus(): MinimalBus {
  if (sharedFallbackBus) return sharedFallbackBus;
  const handlers = new Map<string, Set<(payload: any) => void>>();
  sharedFallbackBus = {
    on(event, handler) {
      let set = handlers.get(event);
      if (!set) {
        set = new Set();
        handlers.set(event, set);
      }
      set.add(handler);
    },
    off(event, handler) {
      const set = handlers.get(event);
      if (set) set.delete(handler);
    },
    emit(event, payload) {
      const set = handlers.get(event);
      if (!set) return;
      // Copy to allow handlers to unsubscribe during dispatch.
      for (const h of new Set(set)) {
        try {
          h(payload);
        } catch {
          // never let one bad subscriber break the dispatch
        }
      }
    },
  };
  return sharedFallbackBus;
}

/** Adapt the canonical `@core/event-bus` EventBus to the MinimalBus shape. */
function wrapHostBus(host: any): MinimalBus {
  return {
    on(event, handler) {
      host.on(event, handler);
    },
    off(event, handler) {
      if (typeof host.off === 'function') {
        host.off(event, handler);
      }
    },
    emit(event, payload) {
      host.emit(event, payload);
    },
  };
}
